import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  signInWithGoogle, signInWithPassword, createAccountWithPassword,
  resetPassword, subscribeAuth, waitForAuth, authErrMsg, readAppUser,
} from "../services/auth";
import { auth } from "../firebase";
import "./Auth.css";

// Same auth pattern as the PlayAware content site + mobile app:
// Google one-tap on top, then email + password. `mode` from the route
// swaps copy, required fields, and the post-auth destination.
//
//   /signin  → returning user; email + password; land on /me
//   /signup  → new user (buyer/reviewer); + display name; land on /me
//   /list    → seller onboarding; + display name + phone; land on /post

const COUNTRIES = [
  { code: "+91", label: "🇮🇳" },
  { code: "+1",  label: "🇺🇸" },
  { code: "+44", label: "🇬🇧" },
  { code: "+61", label: "🇦🇺" },
  { code: "+65", label: "🇸🇬" },
  { code: "+971",label: "🇦🇪" },
  { code: "+49", label: "🇩🇪" },
  { code: "+33", label: "🇫🇷" },
  { code: "+81", label: "🇯🇵" },
  { code: "+82", label: "🇰🇷" },
  { code: "+55", label: "🇧🇷" },
];

const PROFILE_PHONE_KEY = "popmarket-profile-phone";
const PROFILE_NAME_KEY = "popmarket-profile-name";
const PROFILE_EMAIL_KEY = "popmarket-profile-email";
const POST_AUTH_TARGET_KEY = "popmarket-post-auth-target";

function normalizePhone(rawDial, countryCode) {
  const digits = String(rawDial || "").replace(/\D+/g, "");
  if (!digits) return "";
  const cc = countryCode.replace(/\D/g, "");
  const local = digits.startsWith(cc) ? digits.slice(cc.length) : digits;
  return countryCode + local;
}

const isValidPhone = (e164) => /^\+[1-9]\d{7,14}$/.test(e164);

const COPY = {
  signin: {
    title: "Sign in",
    sub: "Welcome back to Pop Market.",
    submit: "Sign in",
    switch: { text: "New here?", label: "Create an account", to: "/signup" },
  },
  signup: {
    title: "Create your account",
    sub: "Free — same account as PlayAware mobile.",
    submit: "Create account",
    switch: { text: "Already have an account?", label: "Sign in", to: "/signin" },
  },
  list: {
    title: "List on Pop Market",
    sub: "Create your seller account. Buyers reach you at the phone number you enter.",
    submit: "Continue to listing",
    switch: { text: "Already a seller?", label: "Sign in instead", to: "/signin" },
  },
};

export default function Auth({ mode = "signin" }) {
  const copy = COPY[mode] || COPY.signin;
  const needsName = mode === "signup" || mode === "list";
  const needsPhone = mode === "list";
  const isCreateFlow = needsName; // signup + list both create accounts

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => subscribeAuth(setUser), []);

  // Redirect if already signed in when landing on this page.
  useEffect(() => {
    waitForAuth().then((u) => {
      if (!u) return;
      redirectAfterAuth();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Post-sign-in redirect when auth state flips to a user.
  useEffect(() => {
    if (!user) return;
    redirectAfterAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Route decision after any sign-in:
  //   1. If the user has never picked seller/buyer, land them on
  //      /role-pick — the current target is stashed so that flow
  //      resumes it after saving.
  //   2. Otherwise honour a stashed target (e.g. the forum page they
  //      came from), or fall back to mode-based default.
  async function redirectAfterAuth() {
    const currentUser = auth.currentUser;
    let target = null;
    try { target = localStorage.getItem(POST_AUTH_TARGET_KEY); } catch (_) {}
    const fallback = mode === "list" ? "/post" : "/me";

    if (currentUser && !currentUser.isAnonymous) {
      try {
        const appUser = await readAppUser(currentUser.uid);
        if (!appUser?.popMarketRole) {
          // Stash the intended destination (target OR mode fallback)
          // so /role-pick can drain it after the user picks a role.
          try {
            localStorage.setItem(POST_AUTH_TARGET_KEY, target || fallback);
          } catch (_) { /* ignore */ }
          navigate("/role-pick", { replace: true });
          return;
        }
      } catch (_) { /* fall through — better to over-route than block */ }
    }

    if (target) {
      try { localStorage.removeItem(POST_AUTH_TARGET_KEY); } catch (_) {}
      navigate(target, { replace: true });
      return;
    }
    navigate(fallback, { replace: true });
  }

  // Stash phone / name / email in localStorage so the Post form pre-
  // fills without asking again.
  function stashProfileHints(e164) {
    try {
      if (email.trim()) localStorage.setItem(PROFILE_EMAIL_KEY, email.trim().toLowerCase());
      if (displayName.trim()) localStorage.setItem(PROFILE_NAME_KEY, displayName.trim());
      if (e164) localStorage.setItem(PROFILE_PHONE_KEY, e164);
    } catch (_) {}
  }

  async function onGoogle() {
    setErr(null); setOk(null); setBusy(true);
    try {
      await signInWithGoogle();
      // redirect happens via useEffect on `user`
    } catch (e) {
      setErr(authErrMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function onEmailSubmit(e) {
    e.preventDefault();
    setErr(null); setOk(null);
    if (!email.trim()) { setErr("Enter your email."); return; }
    if (password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (needsName && !displayName.trim()) {
      setErr("Pick a display name — this is what other people see.");
      return;
    }
    let e164 = "";
    if (phone.trim()) {
      e164 = normalizePhone(phone, countryCode);
      if (!isValidPhone(e164)) {
        setErr("Phone number looks invalid — fix it or leave it empty.");
        return;
      }
    } else if (needsPhone) {
      setErr("A phone number is required to list — that's how buyers reach you.");
      return;
    }
    setBusy(true);
    try {
      if (isCreateFlow) {
        await createAccountWithPassword(email, password, displayName);
      } else {
        await signInWithPassword(email, password);
      }
      stashProfileHints(e164);
    } catch (er) {
      setErr(authErrMsg(er));
    } finally {
      setBusy(false);
    }
  }

  async function onForgot() {
    setErr(null); setOk(null);
    if (!email.trim()) { setErr("Type your email above first, then click Forgot password."); return; }
    setBusy(true);
    try {
      await resetPassword(email);
      setOk(`Password reset email sent to ${email.trim().toLowerCase()}. Check your inbox.`);
    } catch (er) {
      setErr(authErrMsg(er));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1>{copy.title}</h1>
        <p className="auth-sub">{copy.sub}</p>

        <button
          className="auth-google"
          type="button"
          onClick={onGoogle}
          disabled={busy}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.79 2.71v2.25h2.9c1.7-1.56 2.69-3.87 2.69-6.61Z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.46-.81 5.96-2.18l-2.9-2.25c-.8.54-1.83.85-3.06.85-2.36 0-4.36-1.6-5.07-3.74H.92v2.32A9 9 0 0 0 9 18Z"/>
            <path fill="#FBBC05" d="M3.93 10.69A5.4 5.4 0 0 1 3.63 9c0-.58.1-1.16.3-1.69V4.99H.92A9 9 0 0 0 0 9c0 1.45.35 2.82.92 4.01l3.01-2.32Z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 .92 4.99l3.01 2.32C4.64 5.18 6.64 3.58 9 3.58Z"/>
          </svg>
          Continue with Google
        </button>

        <div className="auth-or"><span>or with email</span></div>

        <form className="auth-form" onSubmit={onEmailSubmit} noValidate>
          {needsName && (
            <label>
              <span>Display name</span>
              <input
                type="text"
                autoComplete="nickname"
                maxLength={40}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </label>
          )}
          <label>
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label>
            <span>Password{isCreateFlow ? " (≥ 6 characters)" : ""}</span>
            <input
              type="password"
              autoComplete={isCreateFlow ? "new-password" : "current-password"}
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          {needsPhone && (
            <label>
              <span>Phone number (buyers reach you here)</span>
              <div className="auth-phone-row">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.label} {c.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="9876543210"
                />
              </div>
            </label>
          )}
          <button className="auth-submit" type="submit" disabled={busy}>
            {busy ? "Working…" : copy.submit}
          </button>
          {!isCreateFlow && (
            <button
              type="button"
              className="auth-link-btn"
              onClick={onForgot}
              disabled={busy}
            >
              Forgot password?
            </button>
          )}
        </form>

        {err && <div className="auth-err">{err}</div>}
        {ok && <div className="auth-ok">{ok}</div>}

        <p className="auth-foot">
          {copy.switch.text}{" "}
          <Link to={copy.switch.to}>{copy.switch.label}</Link>
        </p>
      </div>

      <p className="auth-terms">
        By continuing, you agree to our{" "}
        <a href={`${import.meta.env.BASE_URL}about#terms`}>terms</a> and{" "}
        <a href={`${import.meta.env.BASE_URL}about#disclaimer`}>disclaimer</a>.
        PlayAware does not process payments or mediate disputes.
      </p>
    </div>
  );
}
